export type IEntityMap<T> = { [id: string]: T };

export interface ITokenResponse {
  email: string;
}

export interface ILoadingStatus {
  isLoading: boolean;
  hasErrors: boolean;
  errors?: any;
}

export interface IPagination<T> {
  items: T[];
  pageNumber: number;
  totalResults: number;
  pageSize: number;
}

export interface INamedEntity {
  id: string;
  name: string;
  createdAt?: string;
  code?: string;
}

export interface ISelectOption {
  value: any;
  label: string;
}

export enum Gender {
  MALE = 0,
  FEMALE = 1,
}

export enum ToastType {
  SUCCESS = 'success',
  WARN = 'warning',
  ERROR = 'error',
  INFO = 'info',
}

export const genderMap = {
  [Gender.MALE]: 'Male',
  [Gender.FEMALE]: 'Female',
};

export const booleanOptionList = [
  { value: 1, label: 'Yes' },
  { value: 0, label: 'No' },
];

export enum SortingType {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}

export interface IQuery {
  pageNumber?: number;
  pageSize?: number;
  reportIds?: string[];
  projectIds?: string[];
  companyIds?: string[];
  id?: string;
  clientSidePaging?: boolean;
  companyProjectStatus?: string;
  status?: number | string;
  languageCode?: string;
  projectId?: string;
}

export interface IFile {
  id?: string;
  tempId?: string;
  fileName?: string;
  displayName?: string;
  fileSize?: number;
  url?: string;
  image?: string;
  isDeleted?: boolean;
}

export interface IOption {
  value: string;
  label: string;
}

export enum WorkerInvitationStatus {
  ACCEPTED = 0,
  PENDING = 1,
  EXPIRED = 2,
  NOT_INVITED = 3,
  PENDING_INVITATION_OR_MIGRATED = 4,
}

export enum FileStatus {
  INACTIVE = 0,
  PROGRESS = 1,
  SUCCESS = 2,
  FAIL = 3,
}

export interface IDownloadFile {
  newFile?: Blob;
  status: FileStatus;
  error?: any;
}

export enum OnboardingStatus {
  PENDING = 0,
  PROCESSING = 1,
  INCOMPLETE = 2,
  COMPLETED = 3,
}

export enum CompanyProjectStatus {
  PENDING = 0,
  ACCEPTED = 1,
  REJECTED = 2,
  INVITATION_SENT = 3, // For 1 step invitation
  PENDING_WELCOME = 4, // For pending welcome kit
  SUSPENDED = 5,
}

export interface IProjectTimeZone {
  id: string;
  name: string;
  code: string;
  offset: string | null;
  abbreviation: string;
}

export interface IProjectJobSiteAddress {
  id: string;
  line1: string;
  line2: string | null;
  city: string;
  stateCode: string;
  stateName: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  county: string;
  borough: string | null;
  attention1: string | null;
  attention2: string | null;
  country: INamedEntity;
  countryId: string;
}

export interface IProjectConsentFormField {
  consentFormFieldId: string;
  consentFormFieldName: string;
  isVisible: boolean;
  isMandatory: boolean;
  consentFormFieldType: string;
  companyId: string;
  companyName: string;
  order: number;
  isSetOnProjectConsentForm: boolean;
}

export interface IProjectConsentFormLegal {
  languageId: string;
  languageCode: string;
  name: string;
  body: string;
  htmlBody: string;
  id: string;
}

export interface IWorkerCustomProfileField {
  fieldId: string;
  name: string;
  code: string;
  section: string;
  dataType: number;
  isVisibleByDefault: boolean;
  isVisible: boolean;
  isMandatoryByDefault: boolean;
  isMandatory: boolean;
  isSkippableByDefault: boolean;
  isSkippable: boolean;
  skippableTill: number;
  canOverrideVisibleByDefault: boolean;
  canOverrideMandatoryByDefault: boolean;
  canOverrideSkippableByDefault: boolean;
  entityType: number;
}

export interface IProjectWorkerSettings {
  emailPhoneVerificationType: number;
  allowReducedPasswordComplexity: boolean;
  employerMustValidateWorkers: boolean;
  createWorkerRequiresDateOfBirth: boolean;
  workerCustomProfileFields: IWorkerCustomProfileField[];
}

export interface IDrugTestingEntity {
  id: string;
  entityId: string;
  entityName: string;
}

export interface IProject {
  id: string;
  name: string;
  timeZone: IProjectTimeZone;
  jobSiteAddress: IProjectJobSiteAddress;
  status: number;
  consentFormFields: IProjectConsentFormField[];
  consentFormLegals: IProjectConsentFormLegal[];
  workerSettings: IProjectWorkerSettings;
  isDrugTestingActive: boolean;
  drugTestingTypes: IDrugTestingEntity[];
  drugTestingFacilities: IDrugTestingEntity[];
  drugTestingExemptCompanies: IDrugTestingEntity[];
}
